import type { AddLineItemType } from "@/Shared/Types/lineItemTypes";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import type { Contact } from "@freightsimple/generated-dashboard-openapi-client/models/Contact";
import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";
import type { Location } from "@freightsimple/generated-dashboard-openapi-client/models/Location";
import type { PreQuoteLocation } from "@freightsimple/generated-dashboard-openapi-client/models/PreQuoteLocation";
import { LoadingCapabilitiesAvailability } from "@freightsimple/generated-dashboard-openapi-client/models/LoadingCapabilitiesAvailability";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import { EquipmentType } from "@freightsimple/generated-dashboard-openapi-client/models/EquipmentType";
import { QuotesColumn } from "@/Shared/Quotes/CustomComponents/QuotesColumn";
import { QuotesColumnHeader } from "@/Shared/Quotes/CustomComponents/QuotesColumnHeader";
import { QuotesNotesSection } from "@/Shared/Quotes/CustomComponents/QuotesNotesSection";
import moment from "moment";
import { bookShipmentIsWarehouseNotRequiringContact } from "@/Shared/Locations/Helpers/isWarehouseNotRequiringContact";
import { hasLiftGateAccessorial } from "@/Shared/Locations/Helpers/loadingCapabilities";
import {
  ContactSection_,
  EquipmentSection_,
  LoadingCapabilitiesSection_,
  PayloadSection_,
  SpecialServicesSection_,
  WhenSection_,
  WhereSection_,
} from "./Internal/quotesPickupColumnSections";

export interface PickupColumnProps {
  pickupDate: moment.Moment;
  pickupDeadline?: string | undefined;
  pickupLocation: Location | AddLocationLocationType | PreQuoteLocation;
  pickupContact: Contact | undefined;
  lineItems: Array<LineItem | AddLineItemType>;
  quote: { carrierIdentifier: string; serviceDisplayName: string } | undefined;
  shipmentState: ShipmentState;
  pickupReferenceNumber?: string | undefined;
  pickupBoothNumber?: string | undefined;
  notes?: string | undefined;
  equipmentType?: EquipmentType | undefined;
  exclusiveUseNeeded?: boolean | undefined;
  tarpRequired?: boolean | undefined;
  linearFeet?: number | undefined;
  onModifyDetails?: (() => void) | undefined;
  onChangePickupDate?: (() => void) | undefined;
  onModifyPickupAddress?: (() => void) | undefined;
  onModifyPickupContact?: (() => void) | undefined;
  onModifyPickupReferenceAndNotes?: (() => void) | undefined;
  isDomesticCanada: boolean;
  showNotesSection: boolean;
  // Is this in a two column layout?
  twoColumn?: boolean | undefined;
  hidePickupHours?: boolean | undefined;
  pickupLocationHasDock?: LoadingCapabilitiesAvailability | undefined;
  pickupLocationHasForklift?: LoadingCapabilitiesAvailability | undefined;
}

function QuotesPickupColumn(props: PickupColumnProps) {
  const isContactNotRequired = bookShipmentIsWarehouseNotRequiringContact(
    props.pickupLocation.distributionWarehouseBrand,
  );

  return (
    <QuotesColumn twoColumn={props.twoColumn}>
      <div className="flex flex-col flex-nowrap items-start">
        <QuotesColumnHeader>Pickup</QuotesColumnHeader>
        <WhenSection_ {...props} />
        <WhereSection_ {...props} />
        {!isContactNotRequired && <ContactSection_ {...props} />}
        <PayloadSection_ {...props} />
        {props.showNotesSection && (
          <QuotesNotesSection
            reference={props.pickupReferenceNumber}
            boothNumber={props.pickupBoothNumber}
            notes={props.pickupLocation.notes}
            locationType={props.pickupLocation.locationType}
            onModify={props.onModifyPickupReferenceAndNotes}
          />
        )}
        <SpecialServicesSection_ {...props} />
        {!hasLiftGateAccessorial(props.pickupLocation.accessorials) && (
          <LoadingCapabilitiesSection_ {...props} />
        )}
        <EquipmentSection_ {...props} />
      </div>
    </QuotesColumn>
  );
}
export default QuotesPickupColumn;
